/**
 * Authentication Type Definitions
 *
 * TypeScript interfaces and types for the application authentication system.
 * Designed with contractor integration in mind - interfaces can be easily
 * implemented by real authentication services when ready.
 */

// ===== USER MANAGEMENT =====

/**
 * Core user information stored in the system
 */
export interface User {
  /** Unique user identifier */
  id: string;
  /** User's email address (also used as username) */
  email: string;
  /** Display name for the user */
  displayName: string;
  /** Account creation timestamp */
  createdAt: string;
  /** Last login timestamp */
  lastLoginAt: string;
  /** Whether email has been verified */
  emailVerified: boolean;
  /** License key for application access */
  licenseKey: string;
  /** User role (admin, user, etc.) - optional */
  role?: string;
  /** User metadata from Supabase (includes security.reauth_at) - optional */
  user_metadata?: {
    security?: {
      reauth_at?: string;
    };
    [key: string]: unknown;
  };
}

/**
 * Authentication status and session information
 */
export interface AuthState {
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** Current user information (null if not authenticated) */
  user: User | null;
  /** Whether authentication status is being determined */
  isLoading: boolean;
  /** Session token for API requests */
  sessionToken: string | null;
  /** Session expiration timestamp */
  sessionExpiresAt: string | null;
}

// ===== AUTHENTICATION OPERATIONS =====

/**
 * Login request payload
 */
export interface LoginRequest {
  /** User's email address */
  email: string;
  /** User's password */
  password: string;
  /** Whether to keep user logged in across browser sessions */
  rememberMe?: boolean;
}

/**
 * Registration request payload
 */
export interface RegisterRequest {
  /** User's email address */
  email: string;
  /** User's chosen password */
  password: string;
  /** Password confirmation */
  confirmPassword: string;
  /** User's display name */
  displayName: string;
  /** License key for application access */
  licenseKey: string;
}

/**
 * Password reset request payload
 */
export interface PasswordResetRequest {
  /** User's email address */
  email: string;
}

/**
 * Password reset confirmation payload
 */
export interface PasswordResetConfirmRequest {
  /** Reset token from email */
  resetToken: string;
  /** New password */
  newPassword: string;
  /** New password confirmation */
  confirmPassword: string;
}

/**
 * Change password request payload (for authenticated users)
 */
export interface ChangePasswordRequest {
  /** Current password */
  currentPassword: string;
  /** New password */
  newPassword: string;
  /** New password confirmation */
  confirmPassword: string;
}

/**
 * Email change request payload (for authenticated users)
 */
export interface EmailChangeRequest {
  /** New email address */
  newEmail: string;
  /** Current password for verification */
  currentPassword: string;
}

/**
 * Email verification confirmation payload
 */
export interface EmailVerificationRequest {
  /** Verification token from email */
  verificationToken: string;
}

// ===== API RESPONSES =====

/**
 * Successful authentication response
 */
export interface AuthSuccessResponse {
  /** Success flag */
  success: true;
  /** Authenticated user information */
  user: User;
  /** Session token for API requests */
  sessionToken: string;
  /** Session expiration timestamp */
  expiresAt: string;
  /** Optional message for the user */
  message?: string;
}

/**
 * Failed authentication response
 */
export interface AuthErrorResponse {
  /** Success flag */
  success: false;
  /** Error code for programmatic handling */
  error: AuthErrorCode;
  /** Human-readable error message */
  message: string;
  /** Optional field-specific validation errors */
  fieldErrors?: Record<string, string>;
}

/**
 * Generic operation response
 */
export interface AuthOperationResponse {
  /** Success flag */
  success: boolean;
  /** Success or error message */
  message: string;
  /** Optional error code if success is false */
  error?: AuthErrorCode;
  /** Optional data payload for successful operations */
  data?: Record<string, unknown>;
}

// ===== ERROR HANDLING =====

/**
 * Authentication error codes for programmatic handling
 */
export enum AuthErrorCode {
  // Login errors
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  ACCOUNT_NOT_ACTIVATED = 'ACCOUNT_NOT_ACTIVATED',
  USER_NOT_FOUND = 'USER_NOT_FOUND',

  // Registration errors
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  INVALID_LICENSE_KEY = 'INVALID_LICENSE_KEY',
  LICENSE_KEY_ALREADY_USED = 'LICENSE_KEY_ALREADY_USED',
  PASSWORD_TOO_WEAK = 'PASSWORD_TOO_WEAK',

  // Session errors
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_SESSION = 'INVALID_SESSION',

  // Password reset errors
  INVALID_RESET_TOKEN = 'INVALID_RESET_TOKEN',
  RESET_TOKEN_EXPIRED = 'RESET_TOKEN_EXPIRED',

  // Email change errors
  EMAIL_ALREADY_IN_USE = 'EMAIL_ALREADY_IN_USE',
  INVALID_EMAIL_FORMAT = 'INVALID_EMAIL_FORMAT',
  INVALID_VERIFICATION_TOKEN = 'INVALID_VERIFICATION_TOKEN',
  VERIFICATION_TOKEN_EXPIRED = 'VERIFICATION_TOKEN_EXPIRED',
  EMAIL_CHANGE_NOT_PENDING = 'EMAIL_CHANGE_NOT_PENDING',

  // General errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  RATE_LIMITED = 'RATE_LIMITED',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR'
}

/**
 * Field validation error details
 */
export interface ValidationError {
  /** Field name that has the error */
  field: string;
  /** Error message for this field */
  message: string;
  /** Error code for programmatic handling */
  code: string;
}

// ===== SERVICE INTERFACE =====

/**
 * Authentication service interface
 *
 * This interface defines the contract for authentication services.
 * Mock implementation will satisfy this interface, and contractors
 * can implement real authentication services using the same interface.
 */
export interface IAuthService {
  // Core authentication operations
  login(request: LoginRequest): Promise<AuthSuccessResponse | AuthErrorResponse>;
  register(request: RegisterRequest): Promise<AuthSuccessResponse | AuthErrorResponse>;
  logout(): Promise<AuthOperationResponse>;

  // Password management
  requestPasswordReset(request: PasswordResetRequest): Promise<AuthOperationResponse>;
  confirmPasswordReset(request: PasswordResetConfirmRequest): Promise<AuthOperationResponse>;
  changePassword(request: ChangePasswordRequest): Promise<AuthOperationResponse>;

  // Session management
  getCurrentUser(): Promise<User | null>;
  refreshSession(): Promise<AuthSuccessResponse | AuthErrorResponse>;
  validateSession(token: string): Promise<boolean>;

  // Account management
  resendVerificationEmail(email: string): Promise<AuthOperationResponse>;
  verifyEmail(token: string): Promise<AuthOperationResponse>;

  // Email change management
  requestEmailChange(request: EmailChangeRequest): Promise<AuthOperationResponse>;
  verifyEmailChange(request: EmailVerificationRequest): Promise<AuthOperationResponse>;
  cancelEmailChange(): Promise<AuthOperationResponse>;
  resendEmailVerification(): Promise<AuthOperationResponse>;
}

// ===== HOOK TYPES =====

/**
 * Return type for the useAuth hook
 */
export interface UseAuthReturn {
  // Current state
  authState: AuthState;

  // Authentication operations
  login: (request: LoginRequest) => Promise<AuthSuccessResponse | AuthErrorResponse>;
  signup: (credentials: {
    email: string;
    password: string;
    options?: {
      emailRedirectTo?: string;
      data?: Record<string, unknown>;
      captchaToken?: string;
    };
  }) => Promise<{ success: true; userId: string } | { success: false; error: AuthErrorCode; message: string }>;
  register: (request: RegisterRequest) => Promise<AuthSuccessResponse | AuthErrorResponse>;
  logout: () => Promise<void>;
  logoutEverywhere: () => Promise<void>;

  // Password operations
  requestPasswordReset: (request: PasswordResetRequest) => Promise<AuthOperationResponse>;
  confirmPasswordReset: (request: PasswordResetConfirmRequest) => Promise<AuthOperationResponse>;
  changePassword: (request: ChangePasswordRequest) => Promise<AuthOperationResponse>;

  // Profile operations
  updateDisplayName: (newDisplayName: string) => Promise<AuthOperationResponse>;

  // Email change operations
  requestEmailChange: (request: EmailChangeRequest) => Promise<AuthOperationResponse>;
  verifyEmailChange: (request: EmailVerificationRequest) => Promise<AuthOperationResponse>;
  getPendingEmailChange: () => Promise<{ newEmail: string; createdAt: string } | null>;
  cancelEmailChange: () => Promise<AuthOperationResponse>;
  resendEmailVerification: () => Promise<AuthOperationResponse>;

  // Session operations
  // refreshSession: () => Promise<void>; // Handled automatically by Supabase Auth
  clearAuthState: (mode?: 'local' | 'global' | 'revoked') => Promise<void>;

  // Convenience properties
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
}

// ===== MFA RE-EXPORTS =====

/**
 * Re-export MFA types for convenience
 *
 * Allows importing from either @/types/auth or @/types/mfa
 */
export type {
  Factor,
  FactorType,
  FactorStatus,
  EnrollStartResponse,
  ChallengeResponse,
  ReauthTouchResponse,
  MFAError,
  MFAResult,
  MFAOk,
  MFAErr,
} from './mfa';

export { MFAErrorCode } from './mfa';